import React, { useState } from "react";
import "../../Styles/SurveyFeedbackTable.css";
import { useNavigate } from "react-router-dom";
import {
  FaClipboardList,
  FaCheckCircle,
  FaRegClock,
  FaStar,
  FaSearch,
  FaFilter,
  FaMapMarkerAlt,
  FaSyncAlt,
  FaEdit,
  FaRoad,
  FaTint,
  FaExclamationTriangle,
  FaThumbsUp,
  FaSmile,
  FaMeh,
  FaFrown,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";

const CATEGORY_ICON_MAP = {
  "Road Development": <FaRoad />,
  "Drinking Water": <FaTint />,
  "Health Camp": <FaThumbsUp />,
  Electricity: <FaExclamationTriangle />,
  Education: <FaClipboardList />,
  Sanitation: <FaTint />,
  General: <FaClipboardList />,
};

const AVATAR_COLORS = ["sf-avatar-blue", "sf-avatar-orange", "sf-avatar-green"];

const getInitials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const INITIAL_DATA = [
  {
    id: 1,
    name: "Bandaru Nageswara Rao",
    mobile: "9876543210",
    category: "Road Development",
    type: "Complaint",
    rating: "Dissatisfied",
    village: "Gudluru",
    mandal: "Gudluru",
    date: "18-06-2026",
    status: "Pending",
  },
  {
    id: 2,
    name: "Karri Padmavathi",
    mobile: "9123456780",
    category: "Drinking Water",
    type: "Suggestion",
    rating: "Neutral",
    village: "Kandukur",
    mandal: "Kandukur",
    date: "10-06-2026",
    status: "Reviewed",
  },
  {
    id: 3,
    name: "Yerramsetti Bhaskar",
    mobile: "9988776655",
    category: "Health Camp",
    type: "Appreciation",
    rating: "Satisfied",
    village: "Ulavapadu",
    mandal: "Ulavapadu",
    date: "02-06-2026",
    status: "Reviewed",
  },
];

const AdminSurveyFeedbackTable = () => {
  const navigate = useNavigate();

  const [surveyData, setSurveyData] = useState(INITIAL_DATA);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const typeIcons = {
    Complaint: <FaExclamationTriangle />,
    Suggestion: <FaRegClock />,
    Appreciation: <FaThumbsUp />,
  };

  const ratingIcons = {
    Satisfied: <FaSmile />,
    Neutral: <FaMeh />,
    Dissatisfied: <FaFrown />,
  };

  const statusIcons = {
    Reviewed: <FaCheckCircle />,
    Pending: <FaRegClock />,
  };

  /* ===================== SEARCH + FILTER LOGIC ===================== */

  const filteredData = surveyData.filter((item) => {
    const matchesSearch =
      searchTerm.trim() === "" ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.mobile.includes(searchTerm.trim());

    const matchesCategory =
      categoryFilter === "All Categories" || item.category === categoryFilter;

    const matchesMandal =
      mandalFilter === "All Mandals" || item.mandal === mandalFilter;

    const matchesStatus =
      statusFilter === "All Status" || item.status === statusFilter;

    return matchesSearch && matchesCategory && matchesMandal && matchesStatus;
  });

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("All Categories");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const totalEntries = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / 10));
  const safePage = Math.min(currentPage, totalPages);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const startIndex = (safePage - 1) * 10;
  const endIndex = startIndex + 10;
  const pageData = filteredData.slice(startIndex, endIndex);

  /* ===================== EDIT LOGIC ===================== */

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({ ...item });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = (e) => {
    e.preventDefault();
    setSurveyData((prev) =>
      prev.map((item) => (item.id === editForm.id ? { ...editForm } : item)),
    );
    closeEditModal();
  };

  return (
    <div className="sf-container">
      {/* Header */}
      <div className="sf-header">
        <div>
          <h2 className="sf-title">Surveys &amp; Feedback</h2>
          <p className="sf-subtitle">
            Manage and track surveys and feedback submitted by citizens.
          </p>
        </div>

        <button
          className="sf-add-btn"
          onClick={() => navigate("/AdminDashboard/SurveyFeedbackForm")}
        >
          <span className="sf-add-plus">+</span> Add New Feedback
        </button>
      </div>

      {/* Stat Cards */}
      <div className="sf-stats-row">
        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-blue">
            <FaClipboardList />
          </div>
          <div>
            <p className="sf-stat-label">Total Feedback</p>
            <h3 className="sf-stat-value">1,248</h3>
            <p className="sf-stat-sub sf-trend-up">↑ 12.5% this month</p>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-green">
            <FaCheckCircle />
          </div>
          <div>
            <p className="sf-stat-label">Reviewed</p>
            <h3 className="sf-stat-value">842</h3>
            <p className="sf-stat-sub sf-trend-up">↑ 10.2% this month</p>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-orange">
            <FaRegClock />
          </div>
          <div>
            <p className="sf-stat-label">Pending</p>
            <h3 className="sf-stat-value">406</h3>
            <p className="sf-stat-sub sf-trend-up">↑ 8.4% this month</p>
          </div>
        </div>

        <div className="sf-stat-card">
          <div className="sf-stat-icon sf-icon-purple">
            <FaStar />
          </div>
          <div>
            <p className="sf-stat-label">Satisfaction Rate</p>
            <h3 className="sf-stat-value">78%</h3>
            <p className="sf-stat-sub sf-trend-up">↑ 6.3% this month</p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="sf-filter-section">
        <div className="sf-search-wrapper">
          <FaSearch className="sf-search-icon" />
          <input
            type="text"
            placeholder="Search by Name or Mobile..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="sf-select-wrapper">
          <FaFilter className="sf-select-icon" />
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Categories</option>
            <option>Road Development</option>
            <option>Drinking Water</option>
            <option>Electricity</option>
            <option>Health Camp</option>
            <option>Education</option>
            <option>Sanitation</option>
            <option>General</option>
          </select>
        </div>

        <div className="sf-select-wrapper">
          <FaMapMarkerAlt className="sf-select-icon" />
          <select
            value={mandalFilter}
            onChange={(e) => {
              setMandalFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Mandals</option>
            <option>Kandukur</option>
            <option>Gudluru</option>
            <option>Ulavapadu</option>
          </select>
        </div>

        <div className="sf-select-wrapper">
          <FaFilter className="sf-select-icon" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Status</option>
            <option>Reviewed</option>
            <option>Pending</option>
          </select>
        </div>

        <button className="sf-clear-btn" onClick={handleClearFilters}>
          <FaSyncAlt /> Clear Filters
        </button>
      </div>

      {/* Table */}
      <div className="sf-table-wrapper">
        <table className="sf-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Category</th>
              <th>Type</th>
              <th>Rating</th>
              <th>Village</th>
              <th>Mandal</th>
              <th>Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {pageData.length > 0 ? (
              pageData.map((item, index) => (
                <tr key={item.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <div className="sf-name-cell">
                      <span
                        className={`sf-avatar ${
                          AVATAR_COLORS[item.id % AVATAR_COLORS.length]
                        }`}
                      >
                        {getInitials(item.name)}
                      </span>
                      {item.name}
                    </div>
                  </td>
                  <td>{item.mobile}</td>
                  <td>
                    <span className="sf-category-pill">
                      {CATEGORY_ICON_MAP[item.category] || <FaClipboardList />}{" "}
                      {item.category}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`sf-type sf-type-${item.type.toLowerCase()}`}
                    >
                      {typeIcons[item.type]} {item.type}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`sf-rating sf-rating-${item.rating
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {ratingIcons[item.rating]} {item.rating}
                    </span>
                  </td>
                  <td>{item.village}</td>
                  <td>{item.mandal}</td>
                  <td>
                    <span className="sf-date-cell">
                      <FaCalendarAlt /> {item.date}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`sf-status ${
                        item.status === "Reviewed"
                          ? "sf-status-reviewed"
                          : "sf-status-pending"
                      }`}
                    >
                      {statusIcons[item.status]} {item.status}
                    </span>
                  </td>
                  <td className="sf-action-cell">
                    <button
                      className="sf-edit-btn"
                      onClick={() => openEditModal(item)}
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="sf-no-data">
                  No matching feedback found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      <div className="sf-footer">
        <p className="sf-footer-text">
          {totalEntries > 0
            ? `Showing ${startIndex + 1} to ${Math.min(
                endIndex,
                totalEntries,
              )} of ${totalEntries} entries`
            : "Showing 0 entries"}
        </p>

        <div className="sf-pagination">
          <button
            className="sf-page-btn"
            onClick={() => goToPage(safePage - 1)}
            disabled={safePage === 1}
          >
            <FaChevronLeft />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`sf-page-btn ${
                safePage === page ? "sf-page-active" : ""
              }`}
              onClick={() => goToPage(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="sf-page-btn"
            onClick={() => goToPage(safePage + 1)}
            disabled={safePage === totalPages}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {editItem && editForm && (
        <div className="sf-modal-overlay" onClick={closeEditModal}>
          <div
            className="sf-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sf-modal-header">
              <h3>Edit Feedback</h3>
              <button className="sf-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="sf-modal-form" onSubmit={handleEditSave}>
              <div className="sf-form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  required
                />
              </div>

              <div className="sf-form-row">
                <label>Mobile</label>
                <input
                  type="text"
                  value={editForm.mobile}
                  onChange={(e) => handleEditChange("mobile", e.target.value)}
                  maxLength={10}
                  required
                />
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) =>
                      handleEditChange("category", e.target.value)
                    }
                  >
                    <option>Road Development</option>
                    <option>Drinking Water</option>
                    <option>Electricity</option>
                    <option>Health Camp</option>
                    <option>Education</option>
                    <option>Sanitation</option>
                    <option>General</option>
                  </select>
                </div>

                <div className="sf-form-row">
                  <label>Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => handleEditChange("type", e.target.value)}
                  >
                    <option>Complaint</option>
                    <option>Suggestion</option>
                    <option>Appreciation</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Rating</label>
                  <select
                    value={editForm.rating}
                    onChange={(e) =>
                      handleEditChange("rating", e.target.value)
                    }
                  >
                    <option>Satisfied</option>
                    <option>Neutral</option>
                    <option>Dissatisfied</option>
                  </select>
                </div>

                <div className="sf-form-row">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      handleEditChange("status", e.target.value)
                    }
                  >
                    <option>Pending</option>
                    <option>Reviewed</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-grid">
                <div className="sf-form-row">
                  <label>Village</label>
                  <input
                    type="text"
                    value={editForm.village}
                    onChange={(e) =>
                      handleEditChange("village", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="sf-form-row">
                  <label>Mandal</label>
                  <select
                    value={editForm.mandal}
                    onChange={(e) =>
                      handleEditChange("mandal", e.target.value)
                    }
                  >
                    <option>Kandukur</option>
                    <option>Gudluru</option>
                    <option>Ulavapadu</option>
                  </select>
                </div>
              </div>

              <div className="sf-form-row">
                <label>Date</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={editForm.date}
                  onChange={(e) => handleEditChange("date", e.target.value)}
                  required
                />
              </div>

              <div className="sf-modal-actions">
                <button
                  type="button"
                  className="sf-modal-cancel-btn"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="sf-modal-save-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSurveyFeedbackTable;